// The Fern generator stamps a magic placeholder version into generated SDK
// code (package.json "version", src/version.ts SDK_VERSION, the
// X-Fern-SDK-Version / User-Agent headers, pyproject.toml, Cargo.toml, ...).
// The [fern-autoversion] pipeline step then swaps that placeholder for the
// real semver, running BETWEEN replay's prepare and apply phases. The version
// line is therefore pipeline-owned, NOT a customer customization -- replay must
// never capture it into a patch nor let a stored patch pin it.
//
// The reliable anchor: the pristine generation tree replay reads as merge BASE
// still carries the placeholder (autoversion runs after [fern-generated]).
// So "a line whose BASE content carries a placeholder" is autoversion-managed.
// This is only ever true under --version AUTO; a fixed-version SDK has no
// placeholder, so every helper here is a hard no-op for those customers.
//
// Source of truth for the literals: the generator-cli (VersionUtils.ts
// MAGIC_VERSION / MAGIC_VERSION_PYTHON and the Go v-prefixed variant). Kept in
// sync manually; a shared constant is a possible future follow-up.

export const AUTOVERSION_PLACEHOLDERS = [
    "v0.0.0-fern-placeholder", // Go -- a superstring of the default; check first
    "0.0.0-fern-placeholder", // TS/Java/C#/Ruby/PHP/Rust/default
    "0.0.0.dev0", // Python (PEP 440 -- Poetry rejects hyphens)
] as const;

// Matches a semver-ish version token, including the placeholders above and
// v-prefixed / pre-release / build-metadata forms. Single character class (no
// nested quantifiers) so it is linear-time -- no catastrophic backtracking.
const VERSION_TOKEN = /(?:v)?\d+\.\d+\.\d+[0-9A-Za-z.+-]*/g;

// Distinctive printable sentinel a version token is replaced with when
// comparing two lines "modulo the version". Printable ASCII (never a NUL) so
// the source stays a normal text file, and vanishingly unlikely to occur
// verbatim in real SDK source.
const VERSION_MASK = "@@FERN_VERSION@@";

function maskVersions(line: string): string {
    return line.replace(VERSION_TOKEN, VERSION_MASK);
}

/** True when `content` carries an autoversion placeholder (the `--version AUTO` signal). */
export function containsPlaceholder(content: string | null | undefined): boolean {
    if (!content) return false;
    return AUTOVERSION_PLACEHOLDERS.some((p) => content.includes(p));
}

/**
 * Core remap: rewrite each THEIRS line that matches an autoversion-managed line
 * of `genBase` (a placeholder-bearing line, compared modulo the version token)
 * to the corresponding line from `target`. Only full-line matches against a
 * managed line are rewritten, so the customer's real edits -- including edits on
 * lines adjacent to (or sharing a hunk with) a version line -- are untouched.
 *
 * `genBase` is always the pristine generation tree; its placeholders are both
 * the `--version AUTO` signal and the identifier of which lines are managed.
 * No-op when `genBase` has no placeholder (fixed `--version`).
 */
function remapManagedLines(genBase: string, target: string, theirs: string): string {
    if (!containsPlaceholder(genBase)) return theirs;

    const managedMasks = new Set<string>();
    for (const line of genBase.split("\n")) {
        if (AUTOVERSION_PLACEHOLDERS.some((p) => line.includes(p))) managedMasks.add(maskVersions(line));
    }
    if (managedMasks.size === 0) return theirs;

    const targetByMask = new Map<string, string>();
    for (const line of target.split("\n")) {
        const mask = maskVersions(line);
        if (managedMasks.has(mask) && !targetByMask.has(mask)) targetByMask.set(mask, line);
    }

    return theirs
        .split("\n")
        .map((line) => {
            const mask = maskVersions(line);
            const replacement = managedMasks.has(mask) ? targetByMask.get(mask) : undefined;
            return replacement !== undefined && replacement !== line ? replacement : line;
        })
        .join("\n");
}

/**
 * CAPTURE side: reset THEIRS's autoversion-managed lines back to the pristine
 * placeholder (from `genBase`), so a captured `patch_content`/`theirs_snapshot`
 * never pins a concrete version. No-op for fixed `--version`.
 */
export function neutralizeAutoversionTheirs(genBase: string, theirs: string): string {
    return remapManagedLines(genBase, genBase, theirs);
}

/**
 * MERGE side: align THEIRS's autoversion-managed lines to OURS's current values
 * (the freshly-resolved semver), so THEIRS agrees with OURS on the pipeline-owned
 * version lines. This makes the 3-way merge take the current version with no
 * conflict -- even when a customer edit sits adjacent to a version line -- and
 * self-heals a stale version baked into a legacy THEIRS. No-op for fixed
 * `--version` (no placeholder in `genBase`).
 */
export function alignAutoversionToOurs(genBase: string, ours: string, theirs: string): string {
    return remapManagedLines(genBase, ours, theirs);
}
